import React, { Component } from "react";

const BRAND_RED = "#c9140e";
const GENDERS = ["Male", "Female"];
const API_BASE = process.env.REACT_APP_API_BASE_URL;

// ─── Small building blocks ─────────────────────────────────────────────────
class Btn extends Component {
  render() {
    const { children, variant = "primary", onClick, style: s } = this.props;
    const variants = {
      primary: { background: BRAND_RED, color: "#fff" },
      secondary: { background: "#F1F5F9", color: "#475569" },
    };
    return (
      <button
        onClick={onClick}
        style={{
          padding: "9px 16px",
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
          border: "none",
          cursor: "pointer",
          ...variants[variant],
          ...s,
        }}
      >
        {children}
      </button>
    );
  }
}

// ─── Modal ──────────────────────────────────────────────────────────────────
class GenderLimitModal extends Component {
  render() {
    const { isOpen, form, error, onClose, onSave, onFormChange, saving } = this.props;
    if (!isOpen) return null;

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 2000,
        }}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: 12,
            padding: "32px",
            maxWidth: "450px",
            width: "100%",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          }}
        >
          <h3 style={{ margin: "0 0 20px 0", fontSize: 20, fontWeight: 700, color: "#0F172A" }}>
            Add Donation Limit
          </h3>

          {error && (
            <div
              style={{
                background: "#FEE2E2",
                color: "#991B1B",
                fontSize: 13,
                padding: "8px 12px",
                borderRadius: 8,
                marginBottom: 16,
              }}
            >
              {error}
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#475569", marginBottom: 4 }}>
              Gender
            </label>
            <select
              value={form.gender}
              onChange={(e) => onFormChange("gender", e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #E2E8F0",
                borderRadius: 8,
                fontSize: 14,
                boxSizing: "border-box",
                background: "#fff",
              }}
            >
              <option value="">Select gender</option>
              {GENDERS.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#475569", marginBottom: 4 }}>
              Minimum Days Between Donations
            </label>
            <input
              type="number"
              value={form.min_interval_days}
              onChange={(e) => onFormChange("min_interval_days", e.target.value)}
              placeholder="e.g. 90"
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #E2E8F0",
                borderRadius: 8,
                fontSize: 14,
                boxSizing: "border-box",
              }}
            />
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
            <Btn variant="primary" onClick={onSave}>
              {saving ? "Saving..." : "Save"}
            </Btn>
          </div>
        </div>
      </div>
    );
  }
}

// ─── Main List ──────────────────────────────────────────────────────────────
class DonationLimitList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      limits: [],
      loading: false,
      saving: false,
      showModal: false,
      modalError: "",
      form: { gender: "", min_interval_days: "" },
    };

    this.fetchLimits = this.fetchLimits.bind(this);
    this.openAddModal = this.openAddModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.handleFormChange = this.handleFormChange.bind(this);
    this.handleSave = this.handleSave.bind(this);
  }

  componentDidMount() {
    this.fetchLimits();
  }

  fetchLimits() {
    this.setState({ loading: true });
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/donation-limits`)
      .then((res) => res.json())
      .then((data) => {
        this.setState({
          limits: data.status ? data.data : [],
          loading: false,
        });
      })
      .catch(() => {
        this.setState({ limits: [], loading: false });
      });
  }

  openAddModal() {
    this.setState({
      showModal: true,
      modalError: "",
      form: { gender: "", min_interval_days: "" },
    });
  }

  closeModal() {
    if (this.state.saving) return;
    this.setState({ showModal: false, modalError: "" });
  }

  handleFormChange(field, value) {
    this.setState({ form: { ...this.state.form, [field]: value } });
  }

  handleSave() {
    const { form } = this.state;

    if (!form.gender) {
      this.setState({ modalError: "Please select a gender." });
      return;
    }
    if (form.min_interval_days === "" || Number(form.min_interval_days) <= 0) {
      this.setState({ modalError: "Please enter a valid number of days." });
      return;
    }

    this.setState({ saving: true, modalError: "" });

    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/donation-limits/update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          this.setState({ saving: false, showModal: false });
          this.fetchLimits();
        } else {
          this.setState({
            saving: false,
            modalError: data.message || "Failed to save. Please try again.",
          });
        }
      })
      .catch(() => {
        this.setState({ saving: false, modalError: "Network error. Could not save." });
      });
  }

  render() {
    const { limits, loading, saving, showModal, modalError, form } = this.state;

    return (
      <div style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 20,
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>
              Donation Eligibility Limits
            </h1>
            <p style={{ color: "#64748B", fontSize: 14, margin: "8px 0 0" }}>
              Set the minimum gap (in days) required before a donor of each gender can donate again.
            </p>
          </div>
          <Btn variant="primary" onClick={this.openAddModal}>
            + Add Limit
          </Btn>
        </div>

        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #E2E8F0", overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#F8FAFC" }}>
                {["Gender", "Minimum Interval"].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#475569",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      borderBottom: "1px solid #E2E8F0",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={2} style={{ padding: "2.5rem", textAlign: "center", color: "#94A3B8" }}>
                    Loading…
                  </td>
                </tr>
              ) : limits.length === 0 ? (
                <tr>
                  <td colSpan={2} style={{ padding: "2.5rem", textAlign: "center", color: "#94A3B8" }}>
                    No limits set yet.
                  </td>
                </tr>
              ) : (
                limits.map((l, i) => (
                  <tr key={l.gender} style={{ background: i % 2 === 0 ? "#fff" : "#FAFAFA" }}>
                    <td style={{ padding: "14px 16px", fontWeight: 700, fontSize: 15, color: "#0F172A" }}>
                      {l.gender}
                    </td>
                    <td style={{ padding: "14px 16px", fontWeight: 600 }}>{l.min_interval_days} days</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <GenderLimitModal
          isOpen={showModal}
          form={form}
          error={modalError}
          saving={saving}
          onClose={this.closeModal}
          onSave={this.handleSave}
          onFormChange={this.handleFormChange}
        />
      </div>
    );
  }
}

export default DonationLimitList;
